import type { AlgoliaProductType } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'

/**
 * Composable for managing recently viewed products.
 *
 * @param {string} cookieKey - Unique key for storing recently viewed products.
 * @returns {Object} Recently viewed products management utilities.
 * @property {Ref<string[]>} recentlyViewedFromCookie - Reactive reference to recently viewed product SKUs.
 * @property {Function} getProductsRecentlyViewed - Method to retrieve recently viewed products.
 * @property {Function} addProductToRecentlyViewedCookie - Method to add a product to recently viewed.
 *
 * @example
 * const { getProductsRecentlyViewed, addProductToRecentlyViewedCookie } = useRecentlyViewed('my-recently-viewed')
 */
export const useRecentlyViewed = (cookieKey: string) => {
  const algoliaIndex = useAppConfig().currentAlgoliaIndex
  const { skuPrices, fetchSkuPrices } = useProductTileClPrices()

  /**
   * Cookie storage for recently viewed product SKUs with a 5-month expiration.
   *
   * @type {Ref<string[]>}
   * @remarks
   * Stores up to 30 unique product SKUs.
   */
  const recentlyViewedFromCookie = useCookie<string[]>(cookieKey, {
    default: () => [],
    //max age 5 months
    maxAge: 12960000,
  })

  /**
   * Retrieves recently viewed products with optional filtering and limiting.
   *
   * @param {number} [maxItemsToDisplay=4] - Maximum number of items to display.
   * @param {number} [limit=3] - Limit for processing recently viewed items.
   * @param {string[]} [excludedSkus=[]] - SKUs to exclude from the results.
   * @returns {Promise<AlgoliaProductType[]>} Recently viewed products with pricing.
   *
   * @remarks
   * - Filters out excluded SKUs
   * - Limits the number of products
   * - Fetches full product details from Algolia
   * - Adds Commerce Layer pricing information
   *
   * @example
   * const recentProducts = await getProductsRecentlyViewed(6, 5, ['exclude-sku'])
   */
  const getProductsRecentlyViewed = async (
    maxItemsToDisplay = 4,
    limit = 3,
    excludedSkus: string[] = []
  ) =>
    await useAsyncData(`PRODUCTS_RECENTLY_VIEWED_${cookieKey}`, async () => {
      // Filter out excluded SKUs
      const products = excludedSkus.length
        ? recentlyViewedFromCookie.value.filter(
            productSku => !excludedSkus.some(sku => sku === productSku)
          )
        : [...recentlyViewedFromCookie.value]

      // Limit product codes
      const limitedProductCodes =
        products.length > limit
          ? products.slice(0, maxItemsToDisplay)
          : products

      // Fetch Algolia products
      const algoliaProducts = limitedProductCodes.length
        ? await $fetch(`/api/getProducts/${algoliaIndex}`, {
            query: {
              mfcList: limitedProductCodes.join(','),
            },
          })
        : []

      // Filter and process products
      const filteredAlgoliaProducts = algoliaProducts.filter(isNonNullable)
      const skuCodes = filteredAlgoliaProducts.reduce<string[]>(
        (acc, { size }) => {
          const sku = size?.[0].SKU ?? ''
          acc.push(sku)
          return acc
        },
        []
      )

      // Fetch and add pricing information
      await fetchSkuPrices(skuCodes)
      return filteredAlgoliaProducts.map((product): AlgoliaProductType => {
        const skuWithSize = product?.size?.[0].SKU
        return {
          ...product,
          ...(skuWithSize ? { clPrices: skuPrices.value?.[skuWithSize] } : {}),
        }
      })
    })

  /**
   * Adds a product SKU to the recently viewed cookie.
   *
   * @param {string} sku - Product SKU to add.
   *
   * @remarks
   * - Prevents duplicate entries
   * - Maintains a maximum of 30 recently viewed products
   * - New products are added to the beginning of the list
   *
   * @example
   * addProductToRecentlyViewedCookie('product-123')
   */
  const addProductToRecentlyViewedCookie = (sku: string) => {
    if (recentlyViewedFromCookie.value.includes(sku)) return
    recentlyViewedFromCookie.value.unshift(sku)

    if (recentlyViewedFromCookie.value.length <= 30) return
    recentlyViewedFromCookie.value.pop()
  }

  return {
    recentlyViewedFromCookie,
    getProductsRecentlyViewed,
    addProductToRecentlyViewedCookie,
  }
}
